import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { AppContext } from '@prisjakt/ui-app-context';
import graphqlPropTypes from '@prisjakt/graphql-proptypes';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';
import CampaignItem from '@prisjakt/ui-campaign-item';
import ExpandableGrid from '@prisjakt/ui-expandable-grid';
import { bemGenerator } from '@prisjakt/ui-base';
import Currency from '@prisjakt/ui-currency';
import Button from '@prisjakt/ui-button';
import T from '@prisjakt/ui-translate';

import MatchMedia from '../match-media';
import CampaignLink from '../../areas/campaign/components/link';
import {
  trackCampaignLinkClick,
  trackCampaignItemClick,
} from '../../areas/campaign/tracking';
import ErrorBoundary from '../error-boundary';
import { CampaignDealsRootFragment } from '../../types';

import './index.scss';

const styles = bemGenerator('pj-ui-campaign-deals', [
  'button',
  'campaign-link',
]);

const CampaignDeals = props => {
  const { t } = useContext(AppContext);
  const { deals, activeCampaign, campaignLinkQuery } = props;

  if (!deals || deals.length === 0) {
    return null;
  }

  const campaignItem = deal => (
    <CampaignItem
      key={deal.id}
      diffPercentage={deal.price.diffPercentage}
      store={deal.store}
      previousPriceText={t('campaigns', 'deals.price-n-days-ago', {
        /* The date that compareDaysAgo is based on is taken from just after midnight,
                so from a user perspective it makes more sense to say that the comparison is
                with the price from the day *before* that. A bit confusing, but that's how
                it seems to work... */
        n: deal.price.compareDaysAgo + 1,
        price: <Currency key="currency" price={deal.price.previousPrice} />,
      })}
      product={{
        id: deal.product.id,
        name: deal.product.name,
        link: deal.product.pathName,
        image: deal.product.media.first,
        rating: deal.product.userReviewSummary.rating,
        count: deal.product.userReviewSummary.count,
        price:
          deal.product.priceSummary.regular ||
          deal.product.priceSummary.alternative,
        mode: 'grid',
        category: deal.product.category,
      }}
      onClick={() => {
        trackCampaignItemClick(deal);
      }}
    />
  );

  return (
    <ErrorBoundary onError={() => null}>
      <Panel
        header={
          <PanelHeader title={activeCampaign.name}>
            <CampaignLink
              className={styles['campaign-link']}
              queryString={campaignLinkQuery}
              linkContent={t('campaigns', 'link.view-all')}
              campaign={activeCampaign}
              onClick={() => {
                trackCampaignLinkClick(
                  activeCampaign.id,
                  'deals_component_header'
                );
              }}
            />
          </PanelHeader>
        }
        data-test-type="campaign-deals"
      >
        <div className={styles.block}>
          <MatchMedia
            debounce={1000}
            serverSideRender={{ tablet: false }}
            matches={[
              {
                mediaQuery: '(min-width: 640px) and (max-width: 1000px)',
                values: { tablet: true },
              },
              {
                mediaQuery: 'all',
                values: { tablet: false },
              },
            ]}
          >
            {({ tablet }) => (
              <ExpandableGrid
                limit={tablet ? 3 : 4}
                expandText={t('common', 'button.show-more')}
                collapseText=""
                items={deals.map(campaignItem)}
                canCollapse={false}
                expandedActions={
                  <CampaignLink
                    queryString={campaignLinkQuery}
                    linkContent={
                      <Button variant="outlined" color="primary">
                        <T domain="campaigns">link.view-all</T>
                      </Button>
                    }
                    campaign={activeCampaign}
                    data-test="ShowAllCampaignsButton"
                    onClick={() => {
                      trackCampaignLinkClick(
                        activeCampaign.id,
                        'deals_component_show_all'
                      );
                    }}
                  />
                }
              />
            )}
          </MatchMedia>
        </div>
      </Panel>
    </ErrorBoundary>
  );
};

const { string, shape, arrayOf, number } = PropTypes;

CampaignDeals.propTypes = {
  deals: graphqlPropTypes(CampaignDealsRootFragment),
  campaignLinkQuery: string,
  activeCampaign: shape({
    id: number,
    name: string,
    type: string,
    state: string,
    slug: string,
    web_uri: shape({
      mobile: string,
      desktop: string,
      responsive: string,
    }),
    banners: arrayOf(
      shape({
        url: string,
        type: string,
        imageUrl: string,
      })
    ),
  }).isRequired,
};

CampaignDeals.defaultProps = {
  deals: undefined,
  campaignLinkQuery: null,
};

export default connect(state => ({
  activeCampaign: state.globals.campaigns.active.find(c => c.type === 'OFFER'),
}))(memo(CampaignDeals));
